/* eslint-disable no-param-reassign */
import type { Data } from '../../specProcessor';
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as DynamicVoronoiLayerSpec } from '../../../../layer/preset/other/dynamicVoronoi';
import * as utils from '../../../../util';
import {
  BaseSpec,
  ILayerTypeSpec,
  createDataTypeValidator,
  createEncodingValidator,
  createLayerProcessor
} from '../baseProcessor';

export type Spec = BaseSpec;

type DynamicVoronoiLayerType = StandardChartLayerSpec & DynamicVoronoiLayerSpec & ILayerTypeSpec;

// 创建动态沃洛诺伊图数据类型验证器
const validateDynamicVoronoiDataTypes = createDataTypeValidator('dynamicVoronoi', [
  { field: 'x', type: 'string' },
  { field: 'y', type: 'number' },
  { field: 'group', type: ['string', 'date'] }
]);

// 创建动态沃洛诺伊图编码验证器
const validateDynamicVoronoiEncoding = createEncodingValidator([
  { field: 'x' },
  { field: 'y' },
  { field: 'group' }
]);

// 额外的检查函数 - 验证数据结构和数量
const additionalDynamicVoronoiChecks = (layer: DynamicVoronoiLayerType, data: Data) => {
  const { encoding, type } = layer;
  const dataIndex = layer.dataIndex || 0;
  const values = data[dataIndex]?.values || [];

  if (!values.length) {
    return;
  }

  // 检查数据量是否满足要求（至少需要大于1条）
  if (values.length <= 1) {
    utils.logger.warn('warning', `The "${type}" layer requires more than 1 data points`);
  }

  // 检查分组数据是否完整
  const groups = new Set(values.map(item => item[encoding.group]));
  if (groups.size <= 1) {
    utils.logger.warn(
      'warning',
      `The "${type}" layer requires data from multiple time points (group)`
    );
  }

  // 检查每个时间点是否有足够的分类数据
  const categories = new Set(values.map(item => item[encoding.x]));
  if (categories.size <= 1) {
    utils.logger.warn('warning', `The "${type}" layer requires multiple categories (x)`);
  }

  // 检查是否存在异常数据值
  const hasInvalidValue = values.some(item => {
    const value = item[encoding.y];
    return value === undefined || value === null || Number.isNaN(Number(value));
  });

  if (hasInvalidValue) {
    utils.logger.warn(
      'warning',
      `The "${type}" layer contains invalid numeric values in the "y" field`
    );
    layer.__isEncodingValid = false;
  }
};

// 创建并导出动态沃洛诺伊图处理器
export const dynamicVoronoi = createLayerProcessor<DynamicVoronoiLayerType>(
  'dynamicVoronoi',
  validateDynamicVoronoiEncoding,
  validateDynamicVoronoiDataTypes,
  additionalDynamicVoronoiChecks
);
